import React from 'react';

import { Button, Form, Input, message, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NSKeys } from '@app/i18n';
import { ErrorCode } from '@app/models/error';
import { OnboardingState } from '@app/models/users';
import Api from '@app/services/api';
import { getResponseStatus, hasErrorCode } from '@app/services/apiErrors';
import { getFormErrorsFromResponse } from '@app/services/getFormErrorsFromResponse';
import { useApiErrorMessage } from '@app/services/hooks/useApiErrorMessage';
import { useLocalDispatch } from '@app/services/hooks/useRedux';
import { REFRESH_TOKEN_KEY_NAME, TOKEN_KEY_NAME, TOKEN_KEY_REMEMBER } from '@app/services/httpClient';
import { getAppInitialData } from '@app/store/currentSessionReducer';
import { PhoneInput } from '@components/AntFields';

import { ValidationSchema } from '../../data/validation';
import { IFormValues } from '../../interfaces';

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

const tailLayout = {
  wrapperCol: { xs: { span: 24 }, sm: { offset: 8, span: 16 } },
};

const STALE_LINK_CODES: string[] = [
  ErrorCode.COUNTERPARTY_INVITATION_NOT_LIVE,
  ErrorCode.PARTICIPATION_TRANSITION_FORBIDDEN,
];

interface IProps {
  invitationUid: string;
  state: OnboardingState.CreateSpace | OnboardingState.JoinSpace;
  onStale: () => void;
}

export const RegisterForm: React.FC<IProps> = ({ invitationUid, state, onStale }) => {
  const [form] = Form.useForm<IFormValues>();
  const [submitting, setSubmitting] = React.useState(false);
  const dispatch = useLocalDispatch();
  const navigate = useNavigate();
  const pageT = useTranslation(NSKeys.counterpartyInvite);
  const employeesT = useTranslation(NSKeys.employees);
  const changePasswordT = useTranslation(NSKeys.changePassword);
  const validationT = useTranslation(NSKeys.validation);
  const getErrorMessage = useApiErrorMessage();
  const isCreateSpace = state === OnboardingState.CreateSpace;

  const handleError = (err: unknown, values: IFormValues) => {
    if (hasErrorCode(err, [ErrorCode.SCOPE_LEGAL_ENTITY_NOT_UNIQUE])) {
      message.warning(pageT.t('errors.spaceJustCreated'));
      onStale();
      return;
    }
    if (getResponseStatus(err) === 404) {
      message.error(pageT.t('errors.notFound'));
      onStale();
      return;
    }

    const { pointerErrors, formMessages } = getFormErrorsFromResponse(values, err, validationT.t);
    if (pointerErrors.length) {
      form.setFields(pointerErrors);
    }
    if (formMessages.length || !pointerErrors.length) {
      message.error(formMessages[0] || getErrorMessage(err));
    }
    if (hasErrorCode(err, STALE_LINK_CODES)) {
      onStale();
    }
  };

  const handleSubmit = async (values: IFormValues) => {
    setSubmitting(true);
    try {
      const tokens = await Api.users.postRegisterByCounterpartyInvitation({
        invitation_uuid: invitationUid,
        first_name: values.first_name,
        last_name: values.last_name,
        phone: values.phone,
        title: values.title,
        password: values.password,
        scope_title: isCreateSpace ? values.scope_title?.trim() : undefined,
      });
      localStorage.setItem(REFRESH_TOKEN_KEY_NAME, tokens.refresh_token);
      localStorage.setItem(TOKEN_KEY_NAME, tokens.access_token);
      localStorage.setItem(TOKEN_KEY_REMEMBER, 'true');
      dispatch(getAppInitialData(() => navigate('/dashboard/profile')));
    } catch (err) {
      handleError(err, values);
    } finally {
      setSubmitting(false);
    }
  };

  const yupSync = {
    async validator({ field }: any, value: any) {
      await ValidationSchema(validationT.t).validateSyncAt(field, { [field]: value });
    },
  };

  return (
    <Form<IFormValues>
      name="counterparty_invite_form"
      onFinish={handleSubmit}
      form={form}
      autoComplete="off"
      disabled={submitting}
      {...layout}
    >
      {isCreateSpace && (
        <Form.Item<IFormValues>
          name="scope_title"
          required
          label={pageT.t('form.scopeTitle')}
          extra={pageT.t('form.scopeTitleHint')}
          rules={[yupSync]}
        >
          <Input placeholder={pageT.t('form.scopeTitlePlaceholder')} maxLength={255} />
        </Form.Item>
      )}
      <Form.Item<IFormValues> name="first_name" required label={employeesT.t('form.nameLabel')} rules={[yupSync]}>
        <Input placeholder={employeesT.t('form.namePlaceholder')} />
      </Form.Item>
      <Form.Item<IFormValues> name="last_name" required label={employeesT.t('form.surnameLabel')} rules={[yupSync]}>
        <Input placeholder={employeesT.t('form.surnamePlaceholder')} />
      </Form.Item>
      <Form.Item<IFormValues> name="phone" required label={employeesT.t('form.phoneLabel')} rules={[yupSync]}>
        <PhoneInput />
      </Form.Item>
      <Form.Item<IFormValues> name="title" required label={employeesT.t('form.jobtitleLabel')} rules={[yupSync]}>
        <Input placeholder={employeesT.t('form.jobtitlePlaceholder')} />
      </Form.Item>
      <Form.Item<IFormValues>
        name="password"
        required
        label={changePasswordT.t('passwordNewPlaceholder')}
        rules={[yupSync]}
      >
        <Input.Password placeholder={changePasswordT.t('passwordNewLabel')} autoComplete="new-password" />
      </Form.Item>
      <Form.Item<IFormValues>
        name="second_pwd"
        required
        label={changePasswordT.t('passwordRepeatPlaceholder')}
        dependencies={['password']}
        rules={[
          yupSync,
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue('password') === value) {
                return Promise.resolve();
              }
              return Promise.reject(new Error(validationT.t('passwordNotMatch')));
            },
          }),
        ]}
      >
        <Input.Password placeholder={changePasswordT.t('passwordRepeatLabel')} autoComplete="new-password" />
      </Form.Item>
      <Form.Item {...tailLayout}>
        <Row justify="start">
          <Button type="primary" htmlType="submit" loading={submitting}>
            {isCreateSpace ? pageT.t('form.submitCreate') : pageT.t('form.submitJoin')}
          </Button>
        </Row>
      </Form.Item>
    </Form>
  );
};
